import { get, pick, toUpper } from "lodash"
import createModule from "../createModule"
import { getDefaultCurrencyId, getIsIntradayAccountEnabled, getIsOpPro } from "modules/account-root/selectors"
import { getAvailableCurrencyOptions } from "selectors/currency"
import { MODELS_QUERY } from "resources/models"
import { getPortfolio, getModelCurrentDate, getBetaInstrumentMeta, getSortedModels } from "./selectors"
import view from "./view"
import { SUPPORTED_CURRENCIES_QUERY } from "resources/currencies"

export default createModule({
  view,
  externalProps: (state) => ({
    account: state.global.selected.account,
    portfolio: getPortfolio(state),
    currentDate: getModelCurrentDate(state),
    isIntradayAccountEnabled: getIsIntradayAccountEnabled(state),
  }),
  required: ["account"],
  data: [MODELS_QUERY, {
    resource: "editPortfolioDates",
    url: ({ portfolio }) => {
      const modelId = get(portfolio, "modelId")
      if (!modelId) {
        return null
      }

      return {
        id: `edit-portfolio-dates-${modelId}`,
        query: "query ($modelId: String!) { model (id: $modelId) { availability { currentDate } } }",
        variables: { modelId },
      }
    },
  }, {
    resource: "editPortfolio",
    url: ({ account, portfolioId }) => {
      if (!account || !portfolioId) {
        return null
      }

      return `/api/${account}/p/${portfolioId}`
    },
  }, {
    resource: "betaIndex",
    url: ({ portfolio }) => {
      const betaIndexId = get(portfolio, "betaIndexId")
      if (!betaIndexId) {
        return null
      }

      return {
        id: `beta-index-${betaIndexId}`,
        query: `query {
          index(id: "${betaIndexId}") {
            id
            name
            description
          }
        }`,
      }
    },
  }, {
    resource: "betaResource",
    url: ({ account, portfolio }) => {
      if (!portfolio) {
        return null
      }

      const { betaResourceId, betaResourceType } = portfolio
      if (!account || !betaResourceId || !betaResourceType) {
        return null
      }

      return {
        id: `beta-resource-${betaResourceId}-${betaResourceType}`,
        query: `query($type: ResourceType!, $id: IdOrShortIdOrAlias!) {
          resource(type: $type, id: $id) {
            id
            type
            alias
            name
            description
          }
        }`,
        variables: {
          type: toUpper(betaResourceType),
          id: betaResourceId,
        },
      }
    },
  }, {
    resource: "betaSecurity",
    url: ({ account, portfolio, currentDate }) => {
      const modelId = get(portfolio, "modelId")
      if (!account || !modelId || !currentDate) {
        return null
      }

      const securityId = get(portfolio, "betaSecurityId")
      if (!securityId) {
        return null
      }

      return {
        id: `beta-security-${account}-${modelId}-${JSON.stringify(securityId)}-${currentDate}}`,
        query: `query ($modelId: String!, $ticker: String, $mic: String, $internalId: String, $figi: Figi, $bloombergTicker: BloombergTicker, $currentDate: Date!) {
          model(id: $modelId) {
            security(ticker: $ticker, mic: $mic, internalId: $internalId, figi: $figi, bloombergTicker: $bloombergTicker) {
              descriptors(on: $currentDate) {
                name
                ticker
                mic
                figi
                bloombergTicker
                internalId
              }
            }
          }
        }`,
        variables: {
          modelId,
          ...pick(securityId, "ticker", "mic", "internalId", "figi", "bloombergTicker"),
          currentDate,
        },
      }
    },
  }, {
    resource: "benchmarks",
    url: ({ portfolio }) => {
      const modelId = get(portfolio, "modelId")

      if (!modelId) {
        return null
      }

      return {
        id: `benchmarks-${modelId}`,
        query: `query {
          model(id: "${modelId}") {
            benchmarks {
              id
              name
            }
          }
        }`,
      }
    },
  }, SUPPORTED_CURRENCIES_QUERY],
  mapStateToProps: (state, props) => ({
    accountDefaultCurrencyId: getDefaultCurrencyId(state),
    betaInstrumentMeta: getBetaInstrumentMeta(state, props),
    models: getSortedModels(state, props),
    isOpPro: getIsOpPro(state, props),
    supportedCurrencies: getAvailableCurrencyOptions(state),
  }),
})
